import { Link } from "react-router-dom";
import {Mail, ArrowUpRight} from 'lucide-react';
import { useSeoPages } from "@/hooks/useSeoPages";

export default function Footer() {
  const year = new Date().getFullYear();
  const { mergedPages } = useSeoPages();

  // Une page SEO est considérée comme masquée si son override en base la marque
  // explicitement comme inactive (isActive === false). Les pages absentes de
  // mergedPages (pages non-SEO comme "/bnssa" pilier ou "/bnssa/offres-emploi"
  // qui sont des routes applicatives) restent toujours visibles.
  const isPathHidden = (path: string): boolean => {
    const match = (mergedPages || []).find((p) => p?.path === path);
    return match ? match.isActive === false : false;
  };

  const bnssaLinks = [
  { to: "/bnssa/offres-emploi", label: "Offres d'emploi BNSSA" },
  { to: "/bnssa/formations", label: "Formations BNSSA" },
  { to: "/bnssa", label: "Guide BNSSA", bold: true },
  { to: "/bnssa/formation-bnssa", label: "Formation BNSSA" },
  { to: "/bnssa/emploi-bnssa", label: "Emploi BNSSA" },
  { to: "/bnssa/comment-devenir-bnssa", label: "Comment devenir BNSSA" },
  { to: "/bnssa/salaire-bnssa", label: "Salaire BNSSA" },
  { to: "/bnssa/prix-bnssa", label: "Prix BNSSA" },
  { to: "/bnssa/travailler-avec-bnssa", label: "Travailler avec un BNSSA" },
  { to: "/bnssa/bnssa-ou-bpjeps-aan", label: "BNSSA ou BPJEPS AAN" }].filter((l) => !isPathHidden(l.to));


  const mnsLinks = [
  { to: "/maitre-nageur-sauveteur/offres-emploi", label: "Offres d'emploi MNS" },
  { to: "/maitre-nageur-sauveteur/formations", label: "Formations Maître Nageur" },
  { to: "/maitre-nageur-sauveteur", label: "Guide Maître Nageur", bold: true },
  { to: "/maitre-nageur-sauveteur/devenir-mns", label: "Devenir maître nageur" },
  { to: "/maitre-nageur-sauveteur/formation", label: "Formation maître nageur" } ,
  { to: "/maitre-nageur-sauveteur/emploi", label: "Emploi maître nageur" },
  { to: "/maitre-nageur-sauveteur/salaire", label: "Salaire maître nageur" },
  { to: "/maitre-nageur-sauveteur/bpjeps-aan", label: "BPJEPS AAN" },
  { to: "/maitre-nageur-sauveteur/test-bpjeps-aan", label: "Test BPJEPS AAN" }].filter((l) => !isPathHidden(l.to));


  const infoLinks = [
  { to: "/formations", label: "Catalogue Formations" },
  { to: "/faq", label: "FAQ" },
  { to: "/inscription", label: "Créer un compte" },
  { to: "/backoffice", label: "Espace Recruteur" },
  { to: "/mentions-legales", label: "Mentions Légales" },
  { to: "/politique-confidentialite", label: "Politique de Confidentialité" },
  { to: "/cgv", label: "CGU / CGV" },
  { to: "/liens-utiles", label: "Liens utiles" }];


  return (
    <footer className="bg-[#111111] text-gray-400">
      {/* Top band */}
      <div className="border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">

            {/* Brand col */}
            <div className="lg:col-span-1">
              <Link
                to="/"
                aria-label="bnssa-mns.fr — Accueil"
                className="inline-flex items-baseline mb-5 group"
                style={{ fontFamily: "'Archivo', system-ui, sans-serif" }}>

                <span className="text-[#ef4444] font-black text-2xl tracking-[-0.03em]">BNSSA</span>
                <span className="text-gray-600 mx-[3px] font-bold text-2xl">–</span>
                <span className="text-[#3b82f6] font-black text-2xl tracking-[-0.03em]">MNS</span>
                <span className="text-gray-500 text-base font-semibold ml-0.5">.fr</span>
              </Link>
              <p className="text-[13px] text-gray-500 leading-relaxed mb-5 max-w-[240px]">
                La plateforme de référence pour les métiers de la surveillance et de l'enseignement aquatique en France.
              </p>
              <a
                href="mailto:info@bnssa-mns.fr"
                className="flex items-center gap-2 text-[13px] text-gray-500 hover:text-gray-200 transition-colors group">

                <Mail className="w-4 h-4 flex-shrink-0" />
                info@bnssa-mns.fr
                <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            </div>

            {/* BNSSA */}
            <div>
              <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500 mb-5 flex items-center gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full inline-block" />
                Espace BNSSA
              </h3>
              <ul className="space-y-2.5">
                {bnssaLinks.map((l) =>
                <li key={l.to}>
                    <Link
                    to={l.to}
                    className={`text-[13px] transition-colors hover:text-gray-100 ${
                    l.bold ? "text-gray-300 font-medium" : "text-gray-500"}`
                    }>

                      {l.label}
                    </Link>
                  </li>
                )}
              </ul>
            </div>

            {/* MNS */}
            <div>
              <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500 mb-5 flex items-center gap-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full inline-block" />
                Espace MNS
              </h3>
              <ul className="space-y-2.5">
                {mnsLinks.map((l) =>
                <li key={l.to}>
                    <Link
                    to={l.to}
                    className={`text-[13px] transition-colors hover:text-gray-100 ${
                    l.bold ? "text-gray-300 font-medium" : "text-gray-500"}`
                    }>

                      {l.label}
                    </Link>
                  </li>
                )}
              </ul>
            </div>

            {/* Info */}
            <div>
              <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500 mb-5">
                Informations
              </h3>
              <ul className="space-y-2.5">
                {infoLinks.map((l) =>
                <li key={l.to}>
                    <Link
                    to={l.to}
                    className="text-[13px] text-gray-500 hover:text-gray-100 transition-colors">

                      {l.label}
                    </Link>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-[12px] text-gray-600">
          © {year} <span className="text-gray-400 font-medium">bnssa-mns.fr</span> — Tous droits réservés
        </p>
        <p className="text-[12px] text-gray-600">
          Plateforme spécialisée · Métiers aquatiques · France
        </p>
      </div>
    </footer>);

}